import React, {
  createContext,
  useContext,
  useCallback,
  useState,
} from 'react';
import { exceptionRanges as exceptionRangesApi } from '../requests/api/apiClient';

export interface ExceptionRange {
  startDate: string;
  endDate: string;
  chart?: string;
}

export type PendingRangeItem = [number, number];

interface ChartExceptionContextType {
  isExceptionToolActive: boolean;
  pendingRanges: PendingRangeItem[];
  exceptionRanges: ExceptionRange[];
  dataRefreshKey: number;
  activateExceptionTool: () => void;
  addPendingRange: (range: PendingRangeItem) => void;
  clearPendingRanges: () => void;
  setExceptionRanges: (ranges: ExceptionRange[]) => void;
  incrementDataRefreshKey: () => void;
  // Persiste las excepciones en el servidor, actualiza el estado local y dispara el refetch.
  // Único punto de guardado (rango global o punto de gráfico).
  persistExceptionRanges: (sectorId: string, ranges: ExceptionRange[]) => Promise<void>;
}

const ChartExceptionContext = createContext<ChartExceptionContextType | null>(null);

export const useChartExceptionContext = () => useContext(ChartExceptionContext);

interface ChartExceptionProviderProps {
  children: React.ReactNode;
}

export const ChartExceptionProvider: React.FC<ChartExceptionProviderProps> = ({
  children,
}) => {
  const [isExceptionToolActive, setIsExceptionToolActive] = useState(false);
  const [pendingRanges, setPendingRangesState] = useState<PendingRangeItem[]>([]);
  const [exceptionRanges, setExceptionRangesState] = useState<ExceptionRange[]>([]);
  const [dataRefreshKey, setDataRefreshKey] = useState(0);

  const activateExceptionTool = useCallback(() => {
    setIsExceptionToolActive((prev) => {
      if (prev) setPendingRangesState([]);
      return !prev;
    });
  }, []);

  const addPendingRange = useCallback((range: PendingRangeItem) => {
    setPendingRangesState((prev) => [...prev, range]);
  }, []);

  const clearPendingRanges = useCallback(() => {
    setPendingRangesState([]);
  }, []);

  const setExceptionRanges = useCallback((ranges: ExceptionRange[]) => {
    setExceptionRangesState(ranges);
  }, []);

  const incrementDataRefreshKey = useCallback(() => {
    setDataRefreshKey((prev) => prev + 1);
  }, []);

  const persistExceptionRanges = useCallback(
    async (sectorId: string, ranges: ExceptionRange[]) => {
      await exceptionRangesApi.update(sectorId, ranges);
      setExceptionRangesState(ranges);
      setDataRefreshKey((prev) => prev + 1);
    },
    [],
  );

  const value: ChartExceptionContextType = {
    isExceptionToolActive,
    pendingRanges,
    exceptionRanges,
    dataRefreshKey,
    activateExceptionTool,
    addPendingRange,
    clearPendingRanges,
    setExceptionRanges,
    incrementDataRefreshKey,
    persistExceptionRanges,
  };

  return (
    <ChartExceptionContext.Provider value={value}>
      {children}
    </ChartExceptionContext.Provider>
  );
};
